import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Loader2, X, Upload } from "lucide-react";
import { api, formatApiError, imgSrc } from "@/lib/api";

const emptyPost = { title: "", slug: "", category: "General", excerpt: "", content: "", cover_image: "", published: true };

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...editing };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing.id) {
        await api.put(`/admin/posts/${editing.id}`, payload);
        toast.success("Article updated.");
      } else {
        await api.post("/admin/posts", payload);
        toast.success("Article created.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article permanently?")) return;
    try {
      await api.delete(`/admin/posts/${id}`);
      toast.success("Article deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd);
      setEditing((ed) => ({ ...ed, cover_image: data.url }));
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const set = (k) => (e) => setEditing((ed) => ({ ...ed, [k]: e.target.value }));

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-deep animate-spin" /></div>;

  return (
    <div data-testid="blog-manager">
      {!editing ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-xl font-extrabold text-brand-deep">Farm Knowledge Articles ({posts.length})</h2>
            <button onClick={() => { setEditing({ ...emptyPost }); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="post-new-button" className="flex items-center gap-2 rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold text-white hover:bg-brand-forest transition-all">
              <Plus className="w-4 h-4" /> New Article
            </button>
          </div>
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} data-testid={`admin-post-${p.slug}`} className="bg-white rounded-2xl border border-brand-deep/10 p-4 flex items-center gap-4">
                {p.cover_image && <img src={imgSrc(p.cover_image)} alt="" className="w-14 h-14 rounded-xl object-cover bg-brand-beige shrink-0" />}
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-brand-deep truncate">{p.title}</h3>
                  <p className="text-xs text-brand-charcoal/50">{p.category} · {new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${p.published ? "bg-brand-deep/10 text-brand-deep" : "bg-slate-100 text-slate-400"}`}>
                  {p.published ? "Live" : "Draft"}
                </span>
                <button onClick={() => { setEditing({ ...p }); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`post-edit-${p.slug}`} className="w-9 h-9 rounded-full bg-brand-deep/5 border border-brand-deep/15 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(p.id)} data-testid={`post-delete-${p.slug}`} className="w-9 h-9 rounded-full bg-red-50 border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <form onSubmit={save} data-testid="post-editor-form" className="bg-white rounded-3xl border border-brand-deep/10 p-6 sm:p-8 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-extrabold text-brand-deep">{editing.id ? "Edit Article" : "New Article"}</h2>
            <button type="button" onClick={() => setEditing(null)} data-testid="post-editor-cancel" className="w-9 h-9 rounded-full bg-brand-deep/5 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-field">Title *</label>
              <input required className="input-field" data-testid="post-title-input" value={editing.title} onChange={set("title")} />
            </div>
            <div>
              <label className="label-field">Slug *</label>
              <input required className="input-field" value={editing.slug} onChange={set("slug")} placeholder="wheat-farming-guide" />
            </div>
            <div>
              <label className="label-field">Category</label>
              <input className="input-field" value={editing.category} onChange={set("category")} placeholder="Wheat Cultivation" />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={editing.published} onChange={(e) => setEditing((ed) => ({ ...ed, published: e.target.checked }))} className="w-5 h-5 rounded accent-[#14532D]" />
                <span className="text-sm font-semibold text-brand-deep">Published (visible on website)</span>
              </label>
            </div>
          </div>
          <div>
            <label className="label-field">Excerpt</label>
            <input className="input-field" value={editing.excerpt} onChange={set("excerpt")} />
          </div>
          <div>
            <label className="label-field">Cover Image URL</label>
            <div className="flex gap-2">
              <input className="input-field flex-1" value={editing.cover_image} onChange={set("cover_image")} placeholder="https://… or /api/uploads/…" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadCover} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="post-cover-upload" className="flex items-center gap-2 rounded-xl bg-brand-deep/5 border border-brand-deep/15 px-4 text-xs font-bold text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              </button>
            </div>
          </div>
          <div>
            <label className="label-field">Content (blank line between paragraphs, start a line with ## for a heading)</label>
            <textarea rows={14} className="input-field font-mono text-xs" data-testid="post-content-input" value={editing.content} onChange={set("content")} />
          </div>
          <button type="submit" disabled={saving} data-testid="post-save-button" className="rounded-full bg-brand-deep px-8 py-3.5 text-sm font-bold text-white hover:bg-brand-forest transition-all disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Article
          </button>
        </form>
      )}
    </div>
  );
}
